import { useMatches } from '@tanstack/react-router';
import { getStepNameFromMatches } from 'router/utils';

/**
 * A custom hook that extracts the current step name from the matched route in
 * a signup flow.
 *
 * This hook selects over the router's matches to determine which step of the
 * signup process the user is currently on. It maps known route patterns to
 * their corresponding step names via `getStepNameFromMatches`, so param
 * routes (e.g. `/status/$statusScreen`) resolve correctly.
 *
 * @returns The step name corresponding to the current route, or `'unknown'`
 * if the current route doesn't match any known signup step.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const stepName = useCurrentStepName();
 *
 *   if (stepName === 'basic-info') {
 *     // User is on the basic info step
 *   }
 *
 *   if (stepName === 'unknown') {
 *     // User is on a route that doesn't correspond to a signup step
 *   }
 * }
 * ```
 *
 * @example
 * Possible return values based on current route:
 * - `'/basic-info'` → `'basic-info'`
 * - `'/business-info'` → `'business-info'`
 * - `'/activities'` → `'activities'`
 * - `'/status/not-eligible'` → `'status'`
 * - `'/success'` → `'success'`
 * - `'/unknown-route'` → `'unknown'`
 */

export function useCurrentStepName() {
  const stepName = useMatches({
    select: matches => getStepNameFromMatches(matches),
  });
  return stepName ?? 'unknown';
}
